import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateExpenseDTO,
  ExpenseListQueryDTO,
  ExpenseSummaryQueryDTO,
  UpdateExpenseDTO,
} from "@/modules/expenses/domain/contracts";
import { expensesApi } from "./client";

const keys = {
  all: ["expenses"] as const,
  list: (query?: ExpenseListQueryDTO) => [...keys.all, "list", query] as const,
  summary: (query: ExpenseSummaryQueryDTO) =>
    [...keys.all, "summary", query] as const,
  detail: (id: string) => [...keys.all, "detail", id] as const,
};

export function useExpensesQuery(query?: ExpenseListQueryDTO) {
  return useQuery({
    queryKey: keys.list(query),
    queryFn: () => expensesApi.list(query),
  });
}

export function useExpenseSummaryQuery(query: ExpenseSummaryQueryDTO) {
  return useQuery({
    queryKey: keys.summary(query),
    queryFn: () => expensesApi.summary(query),
  });
}

export function useExpenseQuery(id: string) {
  return useQuery({
    queryKey: keys.detail(id),
    queryFn: () => expensesApi.get(id),
    enabled: Boolean(id),
  });
}

export function useCreateExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateExpenseDTO) => expensesApi.create(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useUpdateExpense(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateExpenseDTO) => expensesApi.update(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useDeleteExpense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => expensesApi.delete(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: keys.all });
      qc.invalidateQueries({ queryKey: keys.detail(id) });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}
